import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { toggleReaction, listMessageReactions } from '../../lib/db';

// Small curated picker — matches what users actually use without dragging in
// an emoji-mart dependency.
const QUICK_EMOJIS = ['👍', '❤️', '😄', '🎉', '🙏', '👀', '🔥', '✅'];

function groupReactions(rows) {
  // Group by emoji → { emoji, count, mine, users[] }.
  const out = new Map();
  for (const r of rows || []) {
    if (!out.has(r.emoji)) out.set(r.emoji, { emoji: r.emoji, count: 0, users: [] });
    const g = out.get(r.emoji);
    g.count += 1;
    g.users.push(r.user || { id: r.user_id });
  }
  return Array.from(out.values()).sort((a, b) => b.count - a.count);
}

/**
 * Inline reaction strip rendered under each message bubble. Pass the
 * pre-fetched reactions for THIS message (caller does the batched fetch).
 *
 *   scope     'firm' | 'portal'
 *   messageId UUID of the message
 *   firmId    needed for INSERT RLS
 *   userId    current user's auth uid (to detect own clicks)
 *   reactions array of {emoji, user_id, user}
 *   onChange  optional callback after a successful toggle so the parent
 *             can refresh its reactions map
 */
export default function MessageReactions({ scope, messageId, firmId, userId, reactions, onChange, disabled }) {
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const pickerRef = useRef(null);

  const groups = useMemo(() => groupReactions(reactions), [reactions]);

  // Close the picker on outside click.
  useEffect(() => {
    if (!showPicker) return undefined;
    const onDown = (e) => {
      if (!pickerRef.current?.contains(e.target)) setShowPicker(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [showPicker]);

  const react = async (emoji) => {
    setBusy(true);
    await toggleReaction(scope, messageId, firmId, userId, emoji);
    setBusy(false);
    setShowPicker(false);
    onChange?.();
  };

  if (disabled && groups.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {groups.map((g) => {
        const mine = g.users.some((u) => u?.id === userId);
        return (
          <button
            key={g.emoji}
            onClick={() => react(g.emoji)}
            disabled={busy || disabled}
            title={g.users.map((u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ') || 'Someone').join(', ')}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs transition ${
              mine
                ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-200'
                : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-indigo-400'
            } disabled:opacity-50`}
          >
            <span>{g.emoji}</span>
            <span className="tabular-nums text-[11px]">{g.count}</span>
          </button>
        );
      })}

      {!disabled && (
        <div className="relative" ref={pickerRef}>
          <button
            onClick={() => setShowPicker((v) => !v)}
            title="Add reaction"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <SmilePlus size={13} />
          </button>
          {showPicker && (
            <div className="absolute bottom-full mb-1 left-0 z-30 flex gap-0.5 px-1.5 py-1 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-md">
              {QUICK_EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => react(e)}
                  disabled={busy}
                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 text-base leading-none"
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * useMessageReactions(scope, messageIds)
 * Fetches all reactions for the given message ids in one query and exposes
 * a map keyed by message_id plus a refresh() helper. Returns an empty map
 * if there are no message ids yet (so the caller can render conditionally).
 */
export function useMessageReactions(scope, messageIds) {
  const [byMsg, setByMsg] = useState(() => new Map());

  const refresh = React.useCallback(() => {
    if (!messageIds?.length) { setByMsg(new Map()); return; }
    listMessageReactions(scope, messageIds).then(({ data }) => {
      const next = new Map();
      for (const r of data || []) {
        if (!next.has(r.message_id)) next.set(r.message_id, []);
        next.get(r.message_id).push(r);
      }
      setByMsg(next);
    });
  // We don't memoise messageIds — caller is responsible for stable refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, JSON.stringify(messageIds || [])]);

  useEffect(() => { refresh(); }, [refresh]);

  return { byMsg, refresh };
}
